import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import Style from "./Photography.module.scss";
import { photographyCategories, photographyImages } from "../../info/Photography";

export default function Photography({ innerRef }) {
    const [activeCategory, setActiveCategory] = useState("All");
    const [selectedSlug, setSelectedSlug] = useState(null);
    const [imageRatios, setImageRatios] = useState({});
    const [wallWidth, setWallWidth] = useState(0);
    const wallRef = useRef(null);

    const filteredImages = activeCategory === "All"
        ? photographyImages
        : photographyImages.filter((image) => image.category === activeCategory);

    const activeImageIndex = filteredImages.findIndex((image) => image.slug === selectedSlug);
    const activeImage = activeImageIndex >= 0 ? filteredImages[activeImageIndex] : null;

    useEffect(() => {
        if (!wallRef.current) {
            return undefined;
        }

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];

            if (entry) {
                setWallWidth(entry.contentRect.width);
            }
        });

        observer.observe(wallRef.current);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!activeImage) {
            return undefined;
        }

        function handleKeyDown(event) {
            if (event.key === "Escape") {
                setSelectedSlug(null);
                return;
            }

            if (event.key === "ArrowRight" && activeImageIndex < filteredImages.length - 1) {
                setSelectedSlug(filteredImages[activeImageIndex + 1].slug);
                return;
            }

            if (event.key === "ArrowLeft" && activeImageIndex > 0) {
                setSelectedSlug(filteredImages[activeImageIndex - 1].slug);
            }
        }

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [activeImage, activeImageIndex, filteredImages]);

    function handleCategorySelect(category) {
        setActiveCategory(category);
        setSelectedSlug(null);
    }

    function openPreviousImage() {
        if (activeImageIndex > 0) {
            setSelectedSlug(filteredImages[activeImageIndex - 1].slug);
        }
    }

    function openNextImage() {
        if (activeImageIndex < filteredImages.length - 1) {
            setSelectedSlug(filteredImages[activeImageIndex + 1].slug);
        }
    }

    function handleImageLoad(slug, event) {
        const { naturalWidth, naturalHeight } = event.currentTarget;

        if (!naturalWidth || !naturalHeight) {
            return;
        }

        const nextRatio = naturalHeight / naturalWidth;

        setImageRatios((currentRatios) => {
            if (currentRatios[slug] === nextRatio) {
                return currentRatios;
            }

            return {
                ...currentRatios,
                [slug]: nextRatio
            };
        });
    }

    function imageAspectRatio(slug) {
        const heightToWidthRatio = imageRatios[slug];

        if (!heightToWidthRatio) {
            return 1;
        }

        return 1 / heightToWidthRatio;
    }

    function targetRowHeight() {
        if (wallWidth >= 1180) {
            return 240;
        }

        if (wallWidth >= 820) {
            return 210;
        }

        if (wallWidth >= 560) {
            return 180;
        }

        return 220;
    }

    function buildRows(images) {
        const gap = 16;
        const containerWidth = wallWidth || 960;
        const idealHeight = targetRowHeight();

        if (containerWidth <= 640) {
            return images.map((image) => {
                const aspectRatio = imageAspectRatio(image.slug);
                const width = containerWidth;
                const rowHeight = Math.max(180, Math.round(width / Math.max(aspectRatio, 0.65)));

                return {
                    key: image.slug,
                    items: [
                        {
                            ...image,
                            aspectRatio,
                            width
                        }
                    ],
                    rowHeight,
                    justified: false
                };
            });
        }

        const rows = [];
        let currentRow = [];
        let rowAspectRatioSum = 0;

        images.forEach((image, index) => {
            const aspectRatio = imageAspectRatio(image.slug);
            currentRow.push({ ...image, aspectRatio });
            rowAspectRatioSum += aspectRatio;

            const currentRowWidth = rowAspectRatioSum * idealHeight + gap * (currentRow.length - 1);
            const isLastImage = index === images.length - 1;

            if (currentRowWidth >= containerWidth || isLastImage) {
                const shouldJustify = !isLastImage || currentRow.length > 1;
                const rowHeight = shouldJustify
                    ? Math.max(140, Math.min(idealHeight, (containerWidth - gap * (currentRow.length - 1)) / rowAspectRatioSum))
                    : idealHeight;

                rows.push({
                    key: currentRow.map((item) => item.slug).join("-"),
                    items: currentRow.map((item) => ({
                        ...item,
                        width: Math.min(containerWidth, Math.max(120, Math.round(item.aspectRatio * rowHeight)))
                    })),
                    rowHeight: Math.round(rowHeight),
                    justified: shouldJustify
                });

                currentRow = [];
                rowAspectRatioSum = 0;
            }
        });

        return rows;
    }

    const galleryRows = buildRows(filteredImages);

    return (
        <Box className={Style.page} ref={innerRef} id={"photography"}>


            {photographyImages.length === 0 ? (
                <div className={Style.emptyState}>
                    <h2>No photographs yet</h2>
                    <p>Add images into category folders to populate the wall automatically.</p>
                    <code>src/img/photography/portraits/example-shot.jpg</code>
                </div>
            ) : (
                <>
                    <nav className={Style.filters} aria-label={"Photography categories"}>
                        {photographyCategories.map((category) => (
                            <button
                                key={category}
                                type={"button"}
                                className={`${Style.filterButton} ${activeCategory === category ? Style.activeFilter : ""}`}
                                onClick={() => handleCategorySelect(category)}
                            >
                                {category}
                            </button>
                        ))}
                    </nav>

                    <section className={Style.wall} aria-label={"Photography wall"} ref={wallRef}>
                        {galleryRows.map((row) => (
                            <div
                                key={row.key}
                                className={`${Style.row} ${row.justified ? Style.justifiedRow : Style.trailingRow}`}
                                style={{ height: `${row.rowHeight}px` }}
                            >
                                {row.items.map((image) => (
                                    <button
                                        key={image.slug}
                                        type={"button"}
                                        className={Style.tile}
                                        onClick={() => setSelectedSlug(image.slug)}
                                        style={{ width: `${image.width}px` }}
                                    >
                                        <div className={Style.frame}>
                                            <img
                                                className={Style.image}
                                                src={image.src}
                                                alt={image.title}
                                                loading={"lazy"}
                                                onLoad={(event) => handleImageLoad(image.slug, event)}
                                            />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </section>
                </>
            )}

            {activeImage && (
                <div className={Style.lightbox} onClick={() => setSelectedSlug(null)} role={"presentation"}>
                    <div className={Style.lightboxPanel} onClick={(event) => event.stopPropagation()}>
                        <button
                            type={"button"}
                            className={Style.closeButton}
                            onClick={() => setSelectedSlug(null)}
                            aria-label={"Close photo viewer"}
                        >
                            <i className={"fa fa-times"} />
                        </button>

                        <div className={Style.lightboxMediaWrap}>
                            {activeImageIndex > 0 && (
                                <button
                                    type={"button"}
                                    className={`${Style.navButton} ${Style.navButtonLeft}`}
                                    onClick={openPreviousImage}
                                    aria-label={"Previous photo"}
                                >
                                    <i className={"fa fa-chevron-left"} />
                                </button>
                            )}

                            <img className={Style.lightboxImage} src={activeImage.src} alt={activeImage.title} />

                            {activeImageIndex < filteredImages.length - 1 && (
                                <button
                                    type={"button"}
                                    className={`${Style.navButton} ${Style.navButtonRight}`}
                                    onClick={openNextImage}
                                    aria-label={"Next photo"}
                                >
                                    <i className={"fa fa-chevron-right"} />
                                </button>
                            )}
                        </div>

                        <p className={Style.lightboxCounter}>
                            {activeImageIndex + 1} / {filteredImages.length}
                        </p>
                    </div>
                </div>
            )}
        </Box>
    );
}
